import { useEffect, useRef, useState } from "react";
import { useIsMutating } from "@tanstack/react-query";
import { CheckCircle, Loader2 } from "lucide-react";

/**
 * AutoSaveIndicator (DATA_FLOW.md §"Auto-save indicator UX") — vizuál:
 *
 *   "Ukladá sa…"   pri pending mutation
 *   "Uložené ✓"    2 sekundy po dokončení (fade-out)
 *   (nič)          v idle stave
 *
 * Sleduje globálny `useIsMutating()` z TanStack Query — vyhne sa
 * potrebe samostatného state managementu.
 */
export function AutoSaveIndicator(): JSX.Element | null {
  const isMutating = useIsMutating();
  const [showSaved, setShowSaved] = useState(false);
  const wasMutatingRef = useRef(false);

  useEffect(() => {
    const wasMutating = wasMutatingRef.current;
    wasMutatingRef.current = isMutating > 0;

    // Rising edge: mutation skončila → ukáž "Uložené" 2s
    if (wasMutating && isMutating === 0) {
      setShowSaved(true);
      const handle = setTimeout(() => setShowSaved(false), 2000);
      return () => clearTimeout(handle);
    }
    return undefined;
  }, [isMutating]);

  if (isMutating > 0) {
    return (
      <div
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Ukladá sa…
      </div>
    );
  }

  if (showSaved) {
    return (
      <div
        className="inline-flex items-center gap-1.5 text-sm text-success"
        role="status"
        aria-live="polite"
      >
        <CheckCircle className="h-3.5 w-3.5" />
        Uložené
      </div>
    );
  }

  return null;
}
